import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
// import { pageTitle } from '../../helper';
import Div from "../Div";
// import PageHeading from '../PageHeading';
import SectionHeading from "../SectionHeading";
import Spacing from "../Spacing";
import ContactInfoWidget from "../Widget/ContactInfoWidget";
import { useFormik } from "formik";
import * as Yup from "yup";
import { createContact } from "../../services/contact";
import './dialog.scss';

// Characters allowed in a phone number: digits, +, spaces, slash and dash.
const ALLOWED_PHONE_CHARS = /[^0-9+\s/-]/g;

export default function ContactDialog({ open, setOpen }) {
  const [successMessage, setSuccessMessage] = useState("");
  const [submitError, setSubmitError] = useState("");

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      company: "",
      mobile: "",
      message: "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        .min(2, "Please enter your full name")
        .required("Please enter your full name"),
      email: Yup.string()
        .trim()
        .email("Please enter a valid email address")
        .max(255, "Email address is too long")
        .required("Please enter your email address")
        .test(
          "valid-tld",
          "Please enter a valid email address",
          (value) => {
            const tld = !!value && value.split(".").pop();
            return tld ? tld.length >= 2 : true;
          }
        ),
      company: Yup.string()
        .trim()
        .min(2, "Please enter your company name")
        .max(100, "Company name is too long")
        .required("Please enter your company name"),
      // Optional — but if provided, must look like a real phone number.
      mobile: Yup.string()
        .trim()
        .matches(/^[0-9+\s/-]+$/, {
          message: "Please use only numbers for the phone",
          excludeEmptyString: true,
        })
        .test(
          "min-digits",
          "Please enter a valid mobile number",
          (value) => !value || value.replace(/\D/g, "").length >= 7
        ),
      message: Yup.string()
        .trim()
        .min(10, "Please write a little more (at least 10 characters)")
        .required("Please enter your message"),
    }),
    onSubmit: async (values) => {
      setSubmitError("");
      try {
        const res = await createContact(values);
        if (res.state) {
          formik.resetForm();
          setSuccessMessage("Thanks for contacting us!");
          // Hide the success message and close after 3 seconds
          setTimeout(() => {
            setSuccessMessage("");
            setOpen(false);
          }, 3000);
        } else {
          setSubmitError(
            res.message || "Something went wrong. Please try again."
          );
        }
      } catch (err) {
        setSubmitError("Something went wrong. Please try again.");
      }
    },
  });

  useEffect(() => {
    formik.resetForm();
    setSuccessMessage("");
    setSubmitError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Strip anything that isn't a valid phone character before it enters state,
  // so the user physically cannot type letters into the field.
  const handleMobileChange = (e) => {
    const sanitized = e.target.value.replace(ALLOWED_PHONE_CHARS, "");
    formik.setFieldValue("mobile", sanitized);
  };

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  // Small helper to keep the a11y wiring consistent across fields.
  const errorProps = (field) => ({
    "aria-invalid": Boolean(formik.touched[field] && formik.errors[field]),
    "aria-describedby":
      formik.touched[field] && formik.errors[field]
        ? `${field}-error`
        : undefined,
  });

  const fieldError = (field) =>
    formik.touched[field] && formik.errors[field] ? (
      <p id={`${field}-error`} role="alert" style={{ color: "red" }}>
        {formik.errors[field]}
      </p>
    ) : null;

  return (
    <dialog open={open} className="dialog" aria-label="Get in touch">
      <Div className="row padding">
        <div className="align-end-mobile">
          <button
            type="button"
            aria-label="Close contact form"
            className="close"
            onClick={handleClose}
          />
        </div>
        <Div className="col-lg-6">
          <SectionHeading
            title="Do you have a project <br/>in your mind?"
            subtitle="Get In Touch"
          />
          <Spacing lg="55" md="30" />
          <ContactInfoWidget withIcon />
          <Spacing lg="0" md="50" />
        </Div>
        <Div className="col-lg-6">
          <div className="align-end">
            <button
              type="button"
              aria-label="Close contact form"
              className="close"
              onClick={handleClose}
            />
          </div>
          <form
            onSubmit={formik.handleSubmit}
            className="row padding-top"
            noValidate
          >
            <Div className="col-sm-6">
              <input
                id="name"
                type="text"
                placeholder="Full Name*"
                className="cs-form_field"
                name="name"
                autoComplete="name"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.name}
                {...errorProps("name")}
              />
              {fieldError("name")}
              <Spacing lg="20" md="20" />
            </Div>
            <Div className="col-sm-6">
              <input
                id="email"
                placeholder="Email*"
                type="email"
                inputMode="email"
                className="cs-form_field"
                name="email"
                autoComplete="email"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                {...errorProps("email")}
              />
              {fieldError("email")}
              <Spacing lg="20" md="20" />
            </Div>
            <Div className="col-sm-6">
              <input
                id="company"
                placeholder="Company Name*"
                type="text"
                className="cs-form_field"
                name="company"
                autoComplete="organization"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.company}
                {...errorProps("company")}
              />
              {fieldError("company")}
              <Spacing lg="20" md="20" />
            </Div>
            <Div className="col-sm-6">
              <input
                id="mobile"
                placeholder="Mobile Number (optional)"
                type="tel"
                inputMode="tel"
                className="cs-form_field"
                name="mobile"
                autoComplete="tel"
                onChange={handleMobileChange}
                onBlur={formik.handleBlur}
                value={formik.values.mobile}
                {...errorProps("mobile")}
              />
              {fieldError("mobile")}
              <Spacing lg="20" md="20" />
            </Div>
            <Div className="col-sm-12">
              <textarea
                id="message"
                placeholder="Message*"
                cols="30"
                rows="5"
                className="cs-form_field"
                name="message"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.message}
                {...errorProps("message")}
              ></textarea>
              {fieldError("message")}
              <Spacing lg="25" md="25" />
            </Div>
            {successMessage ? (
              <h3 role="status" aria-live="polite">
                {successMessage}
              </h3>
            ) : null}
            {submitError ? (
              <p role="alert" style={{ color: "red" }}>
                {submitError}
              </p>
            ) : null}
            <Div className="col-sm-12">
              <button
                className="cs-btn cs-style1"
                type="submit"
                disabled={formik.isSubmitting}
              >
                <span>
                  {formik.isSubmitting ? "Sending..." : "Send Message"}
                </span>
                <Icon icon="bi:arrow-right" />
              </button>
            </Div>
          </form>
        </Div>
      </Div>
    </dialog>
  );
}
